/**
 * CXGOOLS-7585 — pure helpers for the "plant -> POD" route schedule.
 *
 * Kept free of Angular / store dependencies so they can be unit-tested in isolation
 * (jest.unit.config.js). The solvers do the async orchestration and delegate the
 * value-mapping and the "is a distinct POD" decision to these functions.
 */

export interface PodRouteSchedule {
  toJobTravelTime: number;
  toPlantTravelTime: number;
  distance: number;
}

export interface CalculatedRoute {
  travelDistance?: number;
  travelTimeToTarget?: number;
  travelTimeFromOrigin?: number;
}

/**
 * Maps a route returned by `/v1/stm/orders/routes/calculated` to the order `schedule` shape.
 * Returns null when the route is missing or carries no usable transit values.
 */
export function mapCalculatedRouteToSchedule(route: CalculatedRoute | null | undefined): PodRouteSchedule | null {
  if (!route || (route.travelDistance == null && route.travelTimeToTarget == null)) {
    return null;
  }
  return {
    toJobTravelTime: route.travelTimeToTarget,
    toPlantTravelTime: route.travelTimeFromOrigin,
    distance: route.travelDistance,
  };
}

/**
 * True when a Point of Delivery different from the jobsite was selected (various-locations order),
 * i.e. we should ask the routes API for the plant -> POD transit.
 */
export function isDistinctPod(podCode: string | null | undefined, jobsiteCode: string | null | undefined): boolean {
  return !!podCode && !!jobsiteCode && podCode !== jobsiteCode;
}

/**
 * Whether the order should fetch the plant -> POD route to build its schedule.
 * `usesReviewFallback` is the solver-specific "In Review without a usable DCO slot" signal
 * (MOO: waiting list; UL: no proposals). We only fetch the route for a distinct POD in that case.
 */
export function shouldUsePodRoute(
  usesReviewFallback: boolean,
  podCode: string | null | undefined,
  jobsiteCode: string | null | undefined,
): boolean {
  return !!usesReviewFallback && isDistinctPod(podCode, jobsiteCode);
}

/**
 * Final schedule decision: use the POD route schedule when available, otherwise keep the base
 * schedule (the DCO-slot/proposals value, or the jobsite fallback) that the selector produced.
 */
export function resolveReviewSchedule(
  baseSchedule: PodRouteSchedule[],
  podRouteSchedule: PodRouteSchedule | null,
): PodRouteSchedule[] {
  return podRouteSchedule ? [podRouteSchedule] : baseSchedule;
}
